import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { KeyRound, Users, ShieldCheck, Trash2, Plus, Loader2, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const card = "bg-white border border-[#1A2B4A]/10 rounded-sm p-4";

export function CambioPassword() {
  const [f, setF] = useState({ vecchia: "", nuova: "", conferma: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (f.nuova.length < 6) return toast.error("La nuova password deve avere almeno 6 caratteri");
    if (f.nuova !== f.conferma) return toast.error("Le password non coincidono");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { vecchia: f.vecchia, nuova: f.nuova });
      toast.success("Password aggiornata");
      setF({ vecchia: "", nuova: "", conferma: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore aggiornamento password"); }
    finally { setBusy(false); }
  };
  return (
    <div className={`${card} mt-6`} data-testid="cambio-password">
      <div className="flex items-center gap-2 mb-4"><KeyRound className="w-5 h-5 text-[#1A2B4A]" /><h3 className="font-serif text-xl text-[#1A2B4A]">Cambia password</h3></div>
      <div className="grid sm:grid-cols-3 gap-3">
        <input data-testid="pw-vecchia" type="password" placeholder="Password attuale" value={f.vecchia} onChange={(e) => setF({ ...f, vecchia: e.target.value })} className="px-3 py-2 border border-[#1A2B4A]/20 rounded-sm text-sm focus:outline-none focus:border-[#C9A84C]" />
        <input data-testid="pw-nuova" type="password" placeholder="Nuova password" value={f.nuova} onChange={(e) => setF({ ...f, nuova: e.target.value })} className="px-3 py-2 border border-[#1A2B4A]/20 rounded-sm text-sm focus:outline-none focus:border-[#C9A84C]" />
        <input data-testid="pw-conferma" type="password" placeholder="Conferma nuova" value={f.conferma} onChange={(e) => setF({ ...f, conferma: e.target.value })} className="px-3 py-2 border border-[#1A2B4A]/20 rounded-sm text-sm focus:outline-none focus:border-[#C9A84C]" />
      </div>
      <button data-testid="pw-save" onClick={save} disabled={busy} className="mt-4 inline-flex items-center gap-2 bg-[#1A2B4A] text-white px-4 py-2 rounded-sm text-sm hover:bg-[#22345c] transition-colors disabled:opacity-50">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Aggiorna password
      </button>
    </div>
  );
}

export function GestioneUtenti() {
  const qc = useQueryClient();
  const { refreshUser, user } = useAuth();
  const { data: users } = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/users")).data });
  const { data: perms } = useQuery({ queryKey: ["roleperms"], queryFn: async () => (await api.get("/roles/perms")).data });
  const permList = perms?.permissions || [];
  const [nu, setNu] = useState({ nome: "", email: "", ruolo: "delegato", password: "" });
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState({}); // { [uid]: { permessi overrides } }
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPwVal, setResetPwVal] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const addUser = async () => {
    if (!nu.nome.trim() || !nu.email.trim()) return toast.error("Nome ed email obbligatori");
    if (nu.password.length < 6) return toast.error("Password minima 6 caratteri");
    setBusy(true);
    try {
      await api.post("/users", nu);
      toast.success("Utente creato");
      setNu({ nome: "", email: "", ruolo: "delegato", password: "" });
      qc.invalidateQueries({ queryKey: ["users"] });
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore creazione utente"); }
    finally { setBusy(false); }
  };
  const salvaAgenda = async (u, patch) => {
    try {
      await api.patch(`/users/${u.id}`, patch);
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["calendari"] });
      qc.invalidateQueries({ queryKey: ["calendar-events"] });
      toast.success("Agenda aggiornata");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const changeRole = async (u, ruolo) => {
    try { await api.patch(`/users/${u.id}`, { ruolo }); qc.invalidateQueries({ queryKey: ["users"] }); toast.success("Ruolo aggiornato"); if (u.email === user?.email) refreshUser(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const resetPw = (u) => { setResetTarget(u); setResetPwVal(""); };
  const doResetPw = async () => {
    if (resetPwVal.length < 6) return toast.error("La password deve avere almeno 6 caratteri");
    setResetBusy(true);
    try { await api.post(`/users/${resetTarget.id}/password`, { nuova: resetPwVal }); toast.success("Password reimpostata"); setResetTarget(null); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setResetBusy(false); }
  };
  const delUser = (u) => setDeleteTarget(u);
  const doDelUser = async () => {
    setDeleteBusy(true);
    try { await api.delete(`/users/${deleteTarget.id}`); qc.invalidateQueries({ queryKey: ["users"] }); toast.success("Utente eliminato"); setDeleteTarget(null); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setDeleteBusy(false); }
  };
  const togglePerm = (u, key) => {
    setEdit((prev) => {
      const cur = prev[u.id] || { ...(u.permessi || {}) };
      return { ...prev, [u.id]: { ...cur, [key]: !cur[key] } };
    });
  };
  const savePermsUser = async (u) => {
    const permessi = { ...(u.permessi || {}), ...(edit[u.id] || {}) };
    setBusy(true);
    try {
      await api.patch(`/users/${u.id}`, { permessi });
      toast.success("Permessi aggiornati");
      qc.invalidateQueries({ queryKey: ["users"] });
      setEdit((p) => { const n = { ...p }; delete n[u.id]; return n; });
      if (u.email === user?.email) refreshUser();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore salvataggio permessi"); }
    finally { setBusy(false); }
  };

  return (
    <div className={`${card} mt-6`} data-testid="gestione-utenti">
      <div className="flex items-center gap-2 mb-2"><Users className="w-5 h-5 text-[#1A2B4A]" /><h3 className="font-serif text-xl text-[#1A2B4A]">Utenti</h3></div>
      <p className="text-sm text-[#4a5568] mb-5">Gli <b>Admin</b> possono fare tutto. Per ogni <b>Delegato</b> puoi abilitare singolarmente le funzioni disponibili.</p>

      <div className="space-y-3 mb-5">
        {(users || []).map((u) => {
          const eff = { ...(u.permessi || {}), ...(edit[u.id] || {}) };
          const dirty = !!edit[u.id];
          return (
            <div key={u.id} data-testid={`user-row-${u.email}`} className="border border-[#1A2B4A]/10 rounded-sm p-3">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[160px]">
                  <div className="text-sm font-medium text-[#1A2B4A]">{u.nome}</div>
                  <div className="text-xs text-[#4a5568]">{u.email}</div>
                </div>
                <select data-testid={`user-role-${u.email}`} value={u.ruolo} onChange={(e) => changeRole(u, e.target.value)}
                  className="px-2 py-1.5 border border-[#1A2B4A]/20 rounded-sm text-sm focus:outline-none focus:border-[#C9A84C]">
                  <option value="admin">Admin</option>
                  <option value="delegato">Delegato</option>
                </select>
                <button data-testid={`user-resetpw-${u.email}`} onClick={() => resetPw(u)} title="Reimposta password" className="p-1.5 rounded-md text-[#1A2B4A] hover:bg-[#f0ede4]"><KeyRound className="w-4 h-4" /></button>
                <button data-testid={`user-delete-${u.email}`} onClick={() => delUser(u)} title="Elimina utente" className="p-1.5 rounded-md text-[#9b2c2c] hover:bg-[#f7e9e9]"><Trash2 className="w-4 h-4" /></button>
              </div>

              <div className="mt-3 pt-3 border-t border-[#1A2B4A]/5" data-testid={`user-agenda-${u.email}`}>
                <div className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6] mb-1.5">Agenda Google</div>
                <div className="flex flex-wrap items-center gap-3">
                  <input data-testid={`user-agenda-nome-${u.email}`} defaultValue={u.agenda_nome || ""}
                    placeholder={`Agenda di ${u.nome}`}
                    onBlur={(e) => { if ((e.target.value || "") !== (u.agenda_nome || "")) salvaAgenda(u, { agenda_nome: e.target.value }); }}
                    className="flex-1 min-w-[200px] px-2.5 py-1.5 text-sm border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C]" />
                  <label className="flex items-center gap-2 text-xs text-[#1A2B4A] cursor-pointer">
                    <input type="checkbox" data-testid={`user-agenda-visibile-${u.email}`}
                      checked={u.agenda_visibile !== false}
                      onChange={(e) => salvaAgenda(u, { agenda_visibile: e.target.checked })}
                      className="accent-[#C9A84C]" />
                    proponibile agli altri utenti
                  </label>
                  <label className="flex items-center gap-1.5 text-xs text-[#1A2B4A]">
                    colore
                    <select data-testid={`user-agenda-colore-${u.email}`} defaultValue={u.agenda_colore || ""}
                      onChange={(e) => salvaAgenda(u, { agenda_colore: e.target.value })}
                      className="px-1.5 py-1 text-xs bg-white border border-[#1A2B4A]/20 rounded-sm focus:outline-none">
                      <option value="">automatico</option>
                      {[["#1A2B4A", "blu notte"], ["#b7791f", "oro"], ["#2f855a", "verde"], ["#2b6cb0", "azzurro"],
                        ["#97266d", "magenta"], ["#9b2c2c", "rosso"], ["#2c7a7b", "petrolio"]].map(([v, n]) => (
                        <option key={v} value={v}>{n}</option>
                      ))}
                    </select>
                    <span className="inline-block w-3 h-3 rounded-full border border-black/10"
                      style={{ background: u.agenda_colore || "#cbd5e0" }} />
                  </label>
                  <label className="flex items-center gap-1.5 text-xs text-[#1A2B4A]">
                    agenda predefinita
                    <select data-testid={`user-agenda-default-${u.email}`} defaultValue={u.agenda_default_user_id || ""}
                      onChange={(e) => salvaAgenda(u, { agenda_default_user_id: e.target.value })}
                      className="px-1.5 py-1 text-xs bg-white border border-[#1A2B4A]/20 rounded-sm focus:outline-none">
                      <option value="">la sua</option>
                      {(users || []).filter((x) => x.id !== u.id).map((x) => (
                        <option key={x.id} value={x.id}>{x.agenda_nome || `Agenda di ${x.nome}`}</option>
                      ))}
                    </select>
                  </label>
                  <span className="text-[11px] text-[#8a93a6]">{u.google_email || "Google non collegato"}</span>
                </div>
              </div>

              {u.ruolo === "admin" ? (
                <div className="mt-2 flex items-center gap-1.5 text-xs text-[#4a5568]"><ShieldCheck className="w-3.5 h-3.5 text-[#C9A84C]" /> Accesso completo a tutte le funzioni</div>
              ) : (
                <div className="mt-3 pt-3 border-t border-[#1A2B4A]/5" data-testid={`user-perms-${u.email}`}>
                  <div className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6] mb-2">Funzioni abilitate</div>
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
                    {permList.map((p) => (
                      <label key={p.key} className="flex items-center gap-2 text-sm text-[#1A2B4A] cursor-pointer">
                        <input data-testid={`uperm-${u.email}-${p.key}`} type="checkbox" checked={!!eff[p.key]} onChange={() => togglePerm(u, p.key)} className="w-4 h-4 accent-[#C9A84C]" />
                        {p.label}
                      </label>
                    ))}
                  </div>
                  {dirty && (
                    <button data-testid={`uperm-save-${u.email}`} onClick={() => savePermsUser(u)} disabled={busy} className="mt-3 inline-flex items-center gap-2 bg-[#1A2B4A] text-white px-3 py-1.5 rounded-sm text-xs hover:bg-[#22345c] transition-colors disabled:opacity-50">
                      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Salva permessi
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="grid sm:grid-cols-4 gap-2">
        <input data-testid="nu-nome" placeholder="Nome" value={nu.nome} onChange={(e) => setNu({ ...nu, nome: e.target.value })} className="px-3 py-2 border border-[#1A2B4A]/20 rounded-sm text-sm focus:outline-none focus:border-[#C9A84C]" />
        <input data-testid="nu-email" placeholder="Email" value={nu.email} onChange={(e) => setNu({ ...nu, email: e.target.value })} className="px-3 py-2 border border-[#1A2B4A]/20 rounded-sm text-sm focus:outline-none focus:border-[#C9A84C]" />
        <select data-testid="nu-ruolo" value={nu.ruolo} onChange={(e) => setNu({ ...nu, ruolo: e.target.value })} className="px-2 py-2 border border-[#1A2B4A]/20 rounded-sm text-sm focus:outline-none focus:border-[#C9A84C]">
          <option value="delegato">Delegato</option>
          <option value="admin">Admin</option>
        </select>
        <input data-testid="nu-password" type="text" placeholder="Password" value={nu.password} onChange={(e) => setNu({ ...nu, password: e.target.value })} className="px-3 py-2 border border-[#1A2B4A]/20 rounded-sm text-sm focus:outline-none focus:border-[#C9A84C]" />
      </div>
      <button data-testid="nu-add" onClick={addUser} disabled={busy} className="mt-4 inline-flex items-center gap-2 bg-[#1A2B4A] text-white px-4 py-2 rounded-sm text-sm hover:bg-[#22345c] transition-colors disabled:opacity-50">
        <Plus className="w-4 h-4" /> Aggiungi utente
      </button>

      <Dialog open={!!resetTarget} onOpenChange={(o) => { if (!o) setResetTarget(null); }}>
        <DialogContent data-testid="reset-pw-dialog" className="border-t-2 border-[#C9A84C] shadow-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-[#1A2B4A]">Reimposta password</DialogTitle>
            <DialogDescription>Imposta una nuova password per <b>{resetTarget?.nome}</b> ({resetTarget?.email}).</DialogDescription>
          </DialogHeader>
          <Input data-testid="reset-pw-input" type="text" autoFocus placeholder="Nuova password (min 6 caratteri)"
            value={resetPwVal} onChange={(e) => setResetPwVal(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && doResetPw()} />
          <DialogFooter>
            <Button variant="outline" data-testid="reset-pw-cancel" onClick={() => setResetTarget(null)}>Annulla</Button>
            <Button data-testid="reset-pw-confirm" onClick={doResetPw} disabled={resetBusy}
              className="bg-[#1A2B4A] text-white hover:bg-[#22345c]">
              {resetBusy && <Loader2 className="w-4 h-4 animate-spin mr-1" />} Reimposta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-[#1A2B4A]">Eliminare l'utente?</AlertDialogTitle>
            <AlertDialogDescription>
              L'utente <b>{deleteTarget?.nome}</b> ({deleteTarget?.email}) verrà eliminato definitivamente e perderà l'accesso. Azione non reversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-user-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-user-confirm" onClick={(e) => { e.preventDefault(); doDelUser(); }} disabled={deleteBusy}
              className="bg-[#9b2c2c] hover:bg-[#7a2222]">
              {deleteBusy && <Loader2 className="w-4 h-4 animate-spin mr-1" />} Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
